/// <amd-module name="TFS/Admin/Groups/Pivots/MemberOf/Components/MemberOfPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { Card } from "VSSUI/Card";
import { ColumnMore, ColumnSelect, ITableColumn, SimpleTableCell, Table, TableColumnStyle, TableRow, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import { MemberOfPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/MemberOfPivotActions";
import * as GroupPageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { PermissionsGroup, IPermissionsViewState, PermissionsGraphGroup, IGroupsViewState } from "TFS/Admin/Groups/Common/Contracts";
import { IOperationStatus, TeamProjectDomainPrefix } from "TFS/Admin/Common/Constants/Constants";
import * as MemberOfSelectors from "TFS/Admin/Groups/Pivots/MemberOf/Redux/MemberOfPivotSelectors";
import { GraphGroup, GraphUser } from "VSS/Platform/Contracts/Graph";
import { WrappedComponent } from "VSS/Platform/Layout";
import { ITableRow, ITableRowDetails } from "VSSUI/Components/Table/Table.Props";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Persona, PersonaSize } from "VSSUI/Persona";

import { aadPrefix } from "TFS/Admin/Common/Constants/Constants";
import { GroupPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { IEntityType, IOperationScope, searchTimeout, IPermissionPage, MemberOfOperation, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getMemberOfAddOperationStatusText, getMultiDeletionOperationStatusText } from "TFS/Admin/Groups/Common/Util/MultiOperationDialogUtil";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { caseInsensitiveContains, localeFormat } from "VSS/Core/Util/String";
import { IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { IDeleteDialogProps } from "TFS/Admin/Common/Controls/DeleteDialog/DeleteDialog.Props";
import { MembershipAddPanelProps } from "TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel.Props";
import { getPermissionsPage } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { IMultiOperationDialogProps } from "TFS/Admin/Common/Controls/MultiOperationDialog/MultiOperationDialog.Props";
import { Pill, PillSize } from "VSSUI/Pill";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";

interface IMemberOfPivotContainerProps extends IFilterablePivotProps {}

interface IMemberOfPivotInitializerOwnProps extends IFilterablePivotOwnProps, IMemberOfPivotContainerProps {
    loading?: boolean;
    groups: ObservableArray<PermissionsGraphGroup>;
    selectedGroup?: PermissionsGroup;
    selectedUser?: GraphUser;
    showDeleteMemberDialog: boolean;
    selectedItem?: PermissionsGraphGroup;
    showMessageCard: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    showAddMemberOfPanel: boolean;
    permissions: { [descriptor: string]: GroupPermissions };
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    multiOperationStatuses: IOperationStatus[];
    lastOperation: MemberOfOperation;
    scopeDescriptor: string | undefined;
    isCollectionNavigationAllowed: boolean;
}

export type IMemberOfPivotInitializerProps = IMemberOfPivotInitializerOwnProps & typeof ActionsToDispatch;

class MemberOfPivotInitializer extends FilterablePivot<IMemberOfPivotInitializerProps> {
    private isRemoveDialogOpen = new ObservableValue<boolean>(false);
    private isMultiSummaryOpen = new ObservableValue<boolean>(false);
    private _selection = new ListSelection({ selectOnFocus: false, multiSelect: true });
    private _personaDetailsProvider: PersonaDetailsProvider;
    private _commandBarItems: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();
    // Non-obvious, so worth a comment: it's important that this ColumnSelect be persisted and not regenerated or the select all header button will be broken
    // on any change to the table's item provider.
    private _columnSelect: ColumnSelect = new ColumnSelect();
    private _itemProvider = new ObservableArray<PermissionsGraphGroup>();
    private _searchDelayTimeoutId: number;

    public constructor(props: IMemberOfPivotInitializerProps) {
        super(props);
        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);
        this.props.setFilterPlaceholder(Resources.FilterUsersAndGroups);
    }

    public componentDidUpdate(prevProps: IMemberOfPivotInitializerProps) {
        this._setCommandBarItems();
        this._selection.subscribe(this._setCommandBarItems);
        this.props.groups.subscribe(this._updateItemProvider);

        const prevDescriptor = prevProps.selectedGroup
            ? prevProps.selectedGroup.descriptor
            : prevProps.selectedUser
            ? prevProps.selectedUser.descriptor
            : "";
        const selectedDescriptor = this.props.selectedGroup
            ? this.props.selectedGroup.descriptor
            : this.props.selectedUser
            ? this.props.selectedUser.descriptor
            : "";
        if (prevDescriptor != selectedDescriptor) {
            this.props.initView(this.props.selectedGroup, this.props.selectedUser, this._getPermissionsPage());
            this.props.filter.reset();
        }

        if (this.props.showMessageCard && this.props.messageCardText !== "" && this.props.messageCardSeverity === MessageCardSeverity.Info) {
            sendToast(this.props.messageCardText, this.props.pageContext);
            this.props.onDismissMessageCard();
        }

        // Narrator not built in FilterablePivot because of the multi-render issue
        if (prevProps.searchResult && prevProps.searchResult.status != SearchStatus.Completed && this._isSearchCompleted()) {
            announce(format(Resources.SearchResultsCount, this.props.searchResult.resultItems.length));
        }
    }

    private _isSearchCompleted() {
        return this.props.searchResult.status == SearchStatus.Completed;
    }

    protected _getItemsName(): string {
        return Resources.MemberofZeroResults;
    }

    protected _isContentReady(): boolean {
        return !this.props.loading && !this._isFiltering();
    }

    protected _onFilterChanged(keyword: string): void {
        this.props.logTelemetry(GroupsComponent.MemberOfPivot, "Filter changed", { Type: this._getSelectedSubjectType() });
        clearTimeout(this._searchDelayTimeoutId);
        this._searchDelayTimeoutId = setTimeout(() => {
            this.props.searchMembers(
                this.props.groups,
                keyword,
                this.props.searchResult ? this.props.searchResult.keyword : "",
                this._getPermissionsPage()
            );
        }, searchTimeout);
    }

    protected _getFilteredContentColumns(): ITableColumn<PermissionsGraphGroup>[] {
        return this.getColumns();
    }

    protected _onRenderRow(index: number, item: PermissionsGraphGroup, details: ITableRowDetails<PermissionsGraphGroup>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        this._itemProvider.value = this.props.groups.value;
        this._selection.clear();
        return this._getRenderElements(this.getColumns(), false);
    }

    private _updateItemProvider = () => {
        this._itemProvider.value = this.props.groups.value;
    };

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        this._itemProvider.value = resultItems;
        this._selection.clear();
        return this._getRenderElements(this._getFilteredContentColumns(), true);
    }

    protected _componentDidMount() {
        if (this.props.selectedGroup || this.props.selectedUser) {
            this.props.initView(this.props.selectedGroup, this.props.selectedUser, this._getPermissionsPage());
        }
        this.props.logTelemetry(GroupsComponent.MemberOfPivot, "Pivot loaded", { Type: this._getSelectedSubjectType() });
    }

    private _setCommandBarItems = () => {
        this._commandBarItems.pop();
        if (this._selection && this._selection.selectedCount > 0) {
            this._commandBarItems.push({
                id: "deleteButton",
                text: Resources.MultiDeleteParentsButtonLabel,
                onActivate: () => {
                    this._multiDeleteOpenDialog();
                },
                disabled: this._isAddParentDisabled()
            });
        } else {
            this._commandBarItems.push({
                important: true,
                id: "addParentButton",
                text: Resources.AddParentButtonLabel,
                onActivate: () => {
                    this.props.openAddMemberOfPanel();
                },
                isPrimary: true,
                disabled: this._isAddParentDisabled()
            });
        }
    };

    private _multiDeleteOpenDialog(): void {
        this.isRemoveDialogOpen.value = true;
    }

    private _multiSummaryOpen(): void {
        this.isMultiSummaryOpen.value = true;
    }

    private _isAddParentDisabled(): boolean {
        const { selectedGroup } = this.props;
        return (
            selectedGroup !== undefined &&
            (selectedGroup.isAadGroup ||
                (selectedGroup.actionPermissions !== undefined && !selectedGroup.actionPermissions.isManageMembershipAllowed))
        );
    }

    private _multiDelete = () => {
        const selected = this.props.selectedGroup ? this.props.selectedGroup : this.props.selectedUser;
        if (selected) {
            const toDelete: PermissionsGraphGroup[] = [];
            for (const range of this._selection.value) {
                for (let i = range.beginIndex; i <= range.endIndex; i++) {
                    toDelete.push(this._itemProvider.value[i]);
                }
            }
            this.props.onMultiDeleteClick(selected, toDelete);
        }
        this.props.filter.reset();
        this.isRemoveDialogOpen.value = false;
    };

    private _getRenderElements(columns: ITableColumn<PermissionsGraphGroup>[], filtered: boolean): JSX.Element {
        return (
            <div>
                {this.props.showMessageCard && this.props.messageCardText != "" && this.props.messageCardSeverity !== MessageCardSeverity.Info ? (
                    <MessageCard
                        className="permissions-card"
                        severity={this.props.messageCardSeverity}
                        onDismiss={() => this.props.onDismissMessageCard()}
                    >
                        {!this.props.multiOperationStatuses || this.props.multiOperationStatuses.length == 0 ? (
                            <span dangerouslySetInnerHTML={createMarkup(this.props.messageCardText)} />
                        ) : null}
                        {this.props.multiOperationStatuses && this.props.multiOperationStatuses.length > 0 ? (
                            <span>
                                <Link
                                    onClick={() => {
                                        this._multiSummaryOpen();
                                    }}
                                >
                                    <span dangerouslySetInnerHTML={createMarkup(this.props.messageCardText + Resources.BulkOperationSummaryLink)} />
                                </Link>
                            </span>
                        ) : null}
                    </MessageCard>
                ) : null}
                <Card
                    className="bolt-table-card"
                    headerCommandBarItems={this._commandBarItems}
                    contentProps={{ contentPadding: false }}
                    titleProps={{
                        text: ((
                            <div className="flex-row flex-center font-size-m rhythm-horizontal-4" aria-live={"polite"}>
                                <span className="text-ellipsis">{Resources.Total}</span>
                                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact} className="flex-center">
                                    {this.props.groups.length}
                                </Pill>
                                {this.props.searchResult && this._isSearchCompleted() && (
                                    <>
                                        <span className="text-ellipsis">{Resources.Filtered}</span>
                                        <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact} className="flex-center">
                                            {this.props.searchResult.resultItems.length}
                                        </Pill>
                                    </>
                                )}
                            </div>
                        ) as unknown) as string
                    }}
                >
                    {this._itemProvider.length > 0 ? (
                        <div className="h-scroll-auto">
                            <Table
                                itemProvider={this._itemProvider}
                                columns={columns}
                                className="permissions-groups-table"
                                singleClickActivation={true}
                                onActivate={this._onRowClicked}
                                selection={this._selection}
                            />
                        </div>
                    ) : filtered ? (
                        this._getZeroData()
                    ) : (
                        <div className="empty-member flex-container">
                            <div>
                                <VssIcon iconName="Group" iconType={VssIconType.fabric} className="empty-member-icon" />
                            </div>
                            <div className="empty-membersof-body">{localeFormat(Resources.EmptyMemberOfBody, this.getGroupName())}</div>
                        </div>
                    )}
                    {this.props.showDeleteMemberDialog && this.props.selectedItem ? (
                        <WrappedComponent<IDeleteDialogProps>
                            wrappedType="ms.vss-admin-web.delete-dialog"
                            dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                            wrappedProps={{
                                onCancel: this._onDeleteDialogCancel,
                                onDeleteItemConfirm: this._onDeleteDialogConfirm,
                                itemToDelete: this.props.selectedItem,
                                dialogTitle: Resources.DeleteMemberOfDialogTitle,
                                confirmationMessage: this.props.selectedGroup
                                    ? Resources.DeleteGroupMemberOfConfirmationMessage
                                    : Resources.DeleteUserMemberOfConfirmationMessage,
                                itemName: this.props.selectedItem.displayName,
                                groupOrUserName: this.getGroupName()
                            }}
                        />
                    ) : null}
                    {this.props.showAddMemberOfPanel ? (
                        <WrappedComponent<MembershipAddPanelProps>
                            wrappedType="ms.vss-admin-web.orggroups-membership-add-panel"
                            dependencies={["ms.vss-admin-web.admin-dialogs-groups-content"]}
                            wrappedProps={{
                                panelTitle: Resources.AddMemberOfPanelTitle,
                                onDismiss: () => this.props.closeAddMemberOfPanel(),
                                pageContext: this.props.pageContext,
                                onSave: this._onSaveMemberOf,
                                description: Resources.AddMemberOfPanelDescription,
                                identityTypes: [IEntityType.Group],
                                peoplePickerPlaceholder: Resources.SearchGroups,
                                peoplePickerLabel: Resources.AddMemberOfPeoplePickerLabel,
                                getPermissions: this._getGroupPermissions,
                                permissions: this.props.permissions,
                                operationScopes: [IOperationScope.IMS],
                                onLogTelemetry: this.props.logTelemetry,
                                checkMembershipExistence: this._checkMembershipExistence,
                                removeMembers: this.props.removeMembers,
                                invalidMembers: this.props.invalidMembers,
                                validMembers: this.props.validMembers
                            }}
                        />
                    ) : null}
                </Card>
                {
                    <Observer isRemoveDialogOpen={this.isRemoveDialogOpen}>
                        {(props: { isRemoveDialogOpen: boolean }) => {
                            return props.isRemoveDialogOpen ? (
                                <WrappedComponent<IDeleteDialogProps>
                                    wrappedType="ms.vss-admin-web.delete-dialog"
                                    dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                                    wrappedProps={{
                                        onCancel: this._onDismissRemoveDialog,
                                        onDeleteItemConfirm: this._multiDelete,
                                        itemToDelete: this.props.selectedItem,
                                        dialogTitle: localeFormat(Resources.RemoveParentDialogTitle, this._getSelectedSubjectType()),
                                        confirmationMessage: Resources.RemoveParentConfirmationMessage,
                                        itemName: this.getGroupName(),
                                        deleteButtonLabel: Resources.RemoveButton
                                    }}
                                />
                            ) : null;
                        }}
                    </Observer>
                }
                {
                    <Observer isMultiSummaryOpen={this.isMultiSummaryOpen}>
                        {(props: { isMultiSummaryOpen: boolean }) => {
                            return props.isMultiSummaryOpen ? (
                                <WrappedComponent<IMultiOperationDialogProps>
                                    wrappedType="ms.vss-admin-web.multi-operation-dialog"
                                    dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                                    wrappedProps={{
                                        title: Resources.ViewStatus,
                                        statusText: this._getOperationStatusText(),
                                        primaryColumnHeading: Resources.MultiOperationNameHeader,
                                        secondaryColumnHeading: Resources.MultiOperationStatusHeader,
                                        onCancel: this._onDismissMultiSummary,
                                        operations: this.props.multiOperationStatuses
                                    }}
                                />
                            ) : null;
                        }}
                    </Observer>
                }
            </div>
        );
    }

    private _getOperationStatusText(): string {
        if (this.props.lastOperation == MemberOfOperation.Delete) {
            return getMultiDeletionOperationStatusText(this.props.multiOperationStatuses);
        } else {
            return getMemberOfAddOperationStatusText(this.props.multiOperationStatuses);
        }
    }

    private _onDismissRemoveDialog = () => {
        this.isRemoveDialogOpen.value = false;
    };

    private _onDismissMultiSummary = () => {
        this.isMultiSummaryOpen.value = false;
        this.props.onDismissMultiSummary();
    };

    private _checkMembershipExistence = (member: IIdentity) => {
        if (this.props.selectedGroup) {
            this.props.checkMembershipExistence(this.props.selectedGroup.descriptor, member);
        } else if (this.props.selectedUser) {
            this.props.checkMembershipExistence(this.props.selectedUser.descriptor, member);
        }
    };

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<PermissionsGraphGroup>) => {
        this.props.logTelemetry(GroupsComponent.MemberOfPivot, "Selected group changed", { Type: this._getSelectedSubjectType() });
        const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;

        const graphSubject = tableRow.data;

        // If we are at project settings, the user is project scoped and the group he clicked on is at collection level, we don't navigate into it
        if (this.props.scopeDescriptor && !this.props.isCollectionNavigationAllowed && !graphSubject.domain.startsWith(TeamProjectDomainPrefix)) {
            if (event) {
                event.preventDefault();
            }
            return;
        }

        if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
            this.props.selectedGroupChanged({
                ...(graphSubject as PermissionsGroup),
                isAadGroup: caseInsensitiveContains(graphSubject.origin, aadPrefix),
                updateImage: false,
                actionPermissions: tableRow.data.actionPermissions,
                isDefaultTeam: false
            });

            // Prevent the default navigation from taking place.
            if (event) {
                event.preventDefault();
            }
        }
    };

    private _getGroupPermissions = (subjectDescriptor: string) => {
        this.props.getMemberOfGroupPermissions(subjectDescriptor, true);
    };

    private getGroupName(): string {
        return this.props.selectedGroup ? this.props.selectedGroup.displayName : this.props.selectedUser ? this.props.selectedUser.displayName : "";
    }

    private getColumns(): ITableColumn<PermissionsGraphGroup>[] {
        return [
            this._columnSelect,
            {
                id: "name",
                name: Resources.NameColumnTitle,
                renderCell: this._renderNameColumn,
                width: new ObservableValue(-50),
                columnStyle: TableColumnStyle.Primary,
                onSize: this._onSizeColumn
            },
            {
                id: "scope",
                name: Resources.UserOrScopeColumnTitle,
                renderCell: this._renderScopeColumn,
                width: new ObservableValue(-50),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            new ColumnMore<PermissionsGraphGroup>((item: PermissionsGraphGroup) => {
                return {
                    id: "sub-menu",
                    items: [
                        {
                            id: "deleteMember",
                            iconProps: {
                                iconName: "Delete"
                            },
                            text: Resources.DeleteMembership,
                            onActivate: () => {
                                this.props.onDeleteMemberClick(item);
                            },
                            disabled: this._isDeleteDisabled(item)
                        }
                    ]
                };
            })
        ];
    }

    private _isDeleteDisabled(item: PermissionsGraphGroup): boolean {
        return (
            (this.props.selectedGroup !== undefined && this.props.selectedGroup.isAadGroup) ||
            (item.actionPermissions !== undefined && !item.actionPermissions.isManageMembershipAllowed)
        );
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGraphGroup>,
        tableItem: PermissionsGraphGroup
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                line1={
                    <Tooltip overflowOnly={true}>
                        <span className="fontWeightSemiBold bolt-table-two-line-cell-item text-ellipsis">{tableItem.displayName}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true}>
                        <span className="fontSizeMS bolt-table-two-line-cell-item secondary-text flex-row flex-baseline text-ellipsis">
                            {tableItem.mailAddress}
                        </span>
                    </Tooltip>
                }
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderImageIcon(tableItem, className)
                }}
            />
        );
    };

    private _renderImageIcon(tableItem: GraphGroup, className?: string | undefined): JSX.Element {
        return (
            <Persona
                className="display-members-persona"
                size={PersonaSize.size40}
                identity={convertGraphMemberToIIdentity(this.props.pageContext, tableItem)}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _renderScopeColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGraphGroup>,
        tableItem: PermissionsGraphGroup
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-users-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{tableItem.principalName.slice(0, tableItem.principalName.indexOf("\\"))}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _onDeleteDialogConfirm = () => {
        const item = this.props.selectedItem;
        this.props.logTelemetry(GroupsComponent.MemberOfPivot, "Delete member of confirm button clicked", {
            Type: this._getSelectedSubjectType()
        });

        if (item && item.descriptor) {
            if (this.props.selectedGroup) {
                this.props.onDeleteDialogConfirm(this.props.selectedGroup, item);
                this.props.clearSelectedIdentity();
            } else if (this.props.selectedUser) {
                this.props.onDeleteDialogConfirm(this.props.selectedUser, item);
            }
        }
    };

    private _onDeleteDialogCancel = () => {
        this.props.logTelemetry(GroupsComponent.MemberOfPivot, "Delete member of cancel button clicked", {
            Type: this._getSelectedSubjectType()
        });
        this.props.onDeleteDialogCancel();
    };

    private _onSaveMemberOf = (subjectIdentities: IIdentity[]) => {
        this.props.logTelemetry(GroupsComponent.GroupMembersPivot, "Save member of button clicked", {
            Type: this._getSelectedSubjectType()
        });
        if (this.props.selectedGroup) {
            this.props.onSaveMemberOf(subjectIdentities, this.props.selectedGroup, this._getPermissionsPage());
            this.props.clearSelectedIdentity();
        } else if (this.props.selectedUser) {
            this.props.onSaveMemberOf(subjectIdentities, this.props.selectedUser, this._getPermissionsPage());
        }
    };

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<PermissionsGraphGroup>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _getSelectedSubjectType(): string {
        return this.props.selectedGroup ? "group" : "user";
    }

    protected _isProjectScope(): boolean {
        return false;
    }

    protected _isCollectionNavigationAllowed(): boolean {
        return this.props.isCollectionNavigationAllowed;
    }

    protected _onExpandSearch(): void {}

    protected _componentWillUnmount() {
        clearTimeout(this._searchDelayTimeoutId);
    }

    private _getPermissionsPage(): IPermissionPage {
        return getPermissionsPage(false, this.props.scopeDescriptor != undefined);
    }
}

// Update component props whenever the store's state changes
function mapStateToProps(
    state: IPermissionsViewState & IGroupsViewState,
    providedProps: IMemberOfPivotContainerProps
): Partial<IMemberOfPivotInitializerOwnProps> {
    return {
        ...providedProps,
        selectedGroup: PageSelectors.getSelectedGroup(state) || GroupPageSelectors.getSelectedGroup(state),
        selectedUser: PageSelectors.getSelectedUser(state),
        scopeDescriptor: PageSelectors.getScopeDescriptor(state),
        groups: MemberOfSelectors.getGroup(state),
        searchResult: MemberOfSelectors.getSearchResult(state),
        showDeleteMemberDialog: MemberOfSelectors.getShowDeleteMemberOfDialog(state),
        selectedItem: MemberOfSelectors.getSelectedItem(state),
        showMessageCard: MemberOfSelectors.getShowMessageCard(state),
        messageCardText: MemberOfSelectors.getMessageCardText(state),
        messageCardSeverity: MemberOfSelectors.getMessageCardSeverity(state),
        showAddMemberOfPanel: MemberOfSelectors.getShowAddMemberOfPanel(state),
        loading: MemberOfSelectors.getLoading(state),
        permissions: MemberOfSelectors.getMemberOfGroupPermissions(state),
        invalidMembers: MemberOfSelectors.getInvalidMembers(state),
        validMembers: MemberOfSelectors.getValidMembers(state),
        multiOperationStatuses: MemberOfSelectors.getMultiOperationStatuses(state),
        lastOperation: MemberOfSelectors.getLastOperation(state),
        isCollectionNavigationAllowed: PageSelectors.isCollectionNavigationAllowed(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: MemberOfPivotActions.initView,
    searchMembers: MemberOfPivotActions.searchMembers,
    onDeleteMemberClick: MemberOfPivotActions.onDeleteMemberClick,
    onDeleteDialogConfirm: MemberOfPivotActions.onDeleteDialogConfirm,
    onDeleteDialogCancel: MemberOfPivotActions.onDeleteDialogCancel,
    onDismissMessageCard: MemberOfPivotActions.onDismissMessageCard,
    closeAddMemberOfPanel: MemberOfPivotActions.closeAddMemberOfPanel,
    onSaveMemberOf: MemberOfPivotActions.onSaveMemberOf,
    logTelemetry: GroupsViewActions.logTelemetry,
    clearSelectedIdentity: GroupsViewActions.clearSelectedIdentity,
    getMemberOfGroupPermissions: MemberOfPivotActions.getMemberOfGroupPermissions,
    selectedGroupChanged: GroupsViewActions.selectedGroupChanged,
    checkMembershipExistence: MemberOfPivotActions.checkMembershipExistence,
    removeMembers: MemberOfPivotActions.removeMembers,
    openAddMemberOfPanel: MemberOfPivotActions.openAddMemberOfPanel,
    onMultiDeleteClick: MemberOfPivotActions.onMultiDeleteClick,
    onDismissMultiSummary: MemberOfPivotActions.onDismissMultiSummary,

    // Groups view actions
    setFilterPlaceholder: GroupsViewActions.setFilterPlaceholder
};

export const ConnectedMemberOfPivotInitializer = connect(mapStateToProps, ActionsToDispatch)(MemberOfPivotInitializer);
